import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@uturn/api';
import { getFetchAllGroupQueryKey, useUpdateGroup } from '@uturn/api/v2';
import Overlay from '@uturn/portal/components/overlay';
import {
	type CarrierGroupFormValues,
	carrierGroupSchema,
} from '@uturn/portal/modules/carriers/schema';
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	sonner,
} from '@uturn/ui-kit';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGroupContext } from '../../context/group-context';
import type { TGroup } from '../../pages/overview';

type Props = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	group: TGroup;
};

const UpdateGroup: React.FC<Props> = ({ open, setOpen, group }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { mutateAsync: mutateUpdateGroup, isPending } = useUpdateGroup();

	const { setGroups, groups } = useGroupContext();

	const form = useForm<CarrierGroupFormValues>({
		resolver: zodResolver(carrierGroupSchema),
		mode: 'onChange',
		defaultValues: {
			name: group.groupName,
		},
	});
	const onSubmitHandler = (data: CarrierGroupFormValues) => {
		mutateUpdateGroup(
			{
				id: group.groupId,
				data: {
					groupName: data.name,
					carrierIds: group.carriers?.map((carrier) => carrier.organizationId),
				},
			},
			{
				onSuccess: async () => {
					await queryClient.refetchQueries({
						queryKey: getFetchAllGroupQueryKey(),
					});

					setGroups((state) => {
						const groupIdx = state.findIndex(
							(row) => row.groupId === group.groupId,
						);
						if (groupIdx === -1) {
							return state;
						}

						const row = groups[groupIdx];
						const result = [...groups];

						result[groupIdx] = {
							...row,
							groupName: data.name,
						};

						return result;
					});

					form.reset({
						name: data.name,
					});

					sonner.success(
						t(
							'update-group.toast.success-message',
							`Group name has been updated to "{{name}}" successfully`,
							{
								name: data.name,
							},
						),
					);
					setOpen(false);
					form.reset({ name: data.name });
				},
				onError: (error) => {
					if (isAxiosError(error)) {
						if (error.response?.status === 409) {
							sonner.error(
								t(
									'create-group.toast.duplicate-error-message',
									'Group "{{name}}" already exists, please choose a different name',
									{
										name: data.name,
									},
								),
							);
							return;
						}
					}

					sonner.error(
						t(
							'toast.generic-error-message',
							'An unexpected error occurred. Please contact support',
						),
					);
				},
			},
		);
	};

	const handleOnOpenChange = (open: boolean) => {
		if (open === false) {
			form.reset();
		}

		setOpen(open);
	};

	return (
		<Dialog open={open} onOpenChange={handleOnOpenChange}>
			<DialogContent>
				{isPending && (
					<Overlay>
						<Icon
							icon={faSpinnerThird}
							spin
							className="text-primary"
							size="xl"
						/>
					</Overlay>
				)}
				<DialogHeader>
					{t('update-group.modal.title', 'Update {{name}}', {
						name: group.groupName,
					})}
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmitHandler)}
						className="flex flex-col gap-4"
					>
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('update-group.modal.form.name.label', 'Name')}
									</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={
								!form.formState.isValid || !form.formState.isDirty || isPending
							}
						>
							{t('update-group.modal.submit', 'Update')}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default UpdateGroup;
